import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, Check, Package } from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Componente Select per ingredienti con ricerca e raggruppamento per categoria
 */
const IngredienteSelect = ({ 
  ingredienti = [], 
  categorie = [], 
  value, 
  onChange, 
  placeholder = "Seleziona ingrediente...",
  disabled = false,
  showPrice = true // Mostra prezzo accanto al nome
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  
  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Crea mappa categorie
  const categorieMap = {};
  categorie.forEach(c => {
    categorieMap[c.id] = c.nome;
  });

  // Filtra ingredienti per ricerca
  const ingredientiFiltered = ingredienti.filter(i => 
    i.nome?.toLowerCase().includes(search.toLowerCase())
  );

  // Raggruppa ingredienti per categoria
  const ingredientiGrouped = {};
  ingredientiFiltered.forEach(i => {
    const categoria = categorieMap[i.categoria_id] || "Senza categoria";
    if (!ingredientiGrouped[categoria]) {
      ingredientiGrouped[categoria] = [];
    }
    ingredientiGrouped[categoria].push(i);
  });

  // Ordina gruppi alfabeticamente e ingredienti all'interno di ogni gruppo
  const sortedGroups = Object.keys(ingredientiGrouped).sort((a, b) => {
    // "Senza categoria" sempre per ultimo
    if (a === "Senza categoria") return 1;
    if (b === "Senza categoria") return -1;
    return a.localeCompare(b, 'it');
  });

  // Ordina ingredienti in ogni gruppo
  sortedGroups.forEach(group => {
    ingredientiGrouped[group].sort((a, b) => 
      (a.nome || '').localeCompare(b.nome || '', 'it')
    );
  });

  // Trova ingrediente selezionato
  const selectedIngrediente = ingredienti.find(i => i.id === value);
  
  // Display label
  const displayLabel = selectedIngrediente 
    ? (showPrice 
        ? `${selectedIngrediente.nome} - €${(selectedIngrediente.prezzo_attuale || 0).toFixed(2)}/${selectedIngrediente.unita_misura || 'kg'}`
        : selectedIngrediente.nome)
    : placeholder;

  const handleSelect = (ingrediente) => {
    onChange(ingrediente.id, ingrediente);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background text-left",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-muted-foreground"
        )}
        data-testid="ingrediente-select-trigger"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform flex-shrink-0", open && "rotate-180")} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg" data-testid="ingrediente-select-dropdown">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca ingrediente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
                data-testid="ingrediente-search-input"
              />
            </div>
          </div>

          {/* Lista ingredienti raggruppati */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {sortedGroups.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nessun ingrediente trovato
              </div>
            ) : (
              sortedGroups.map(gruppo => (
                <div key={gruppo}>
                  {/* Header gruppo (categoria) */}
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50 sticky top-0">
                    <Package className="h-3 w-3" />
                    {gruppo}
                    <span className="text-[10px] font-normal">({ingredientiGrouped[gruppo].length})</span>
                  </div>
                  
                  {/* Ingredienti del gruppo */}
                  {ingredientiGrouped[gruppo].map(ingrediente => (
                    <button
                      key={ingrediente.id}
                      onClick={() => handleSelect(ingrediente)}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer",
                        "hover:bg-accent hover:text-accent-foreground",
                        value === ingrediente.id && "bg-accent"
                      )}
                      data-testid={`ingrediente-option-${ingrediente.id}`}
                    >
                      <span className="truncate">{ingrediente.nome}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {showPrice && (
                          <span className="text-xs text-muted-foreground">
                            €{(ingrediente.prezzo_attuale || 0).toFixed(2)}/{ingrediente.unita_misura || 'kg'}
                          </span>
                        )}
                        {value === ingrediente.id && <Check className="h-4 w-4" />}
                      </div>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IngredienteSelect;
